import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import { useDocumentTypes } from '../../schema/hooks/useDocumentTypes';
import {
  WEBHOOK_EVENTS,
  WEBHOOK_EVENT_LABELS,
  type UpdateWebhookSubscriptionRequest,
  type WebhookEventName,
  type WebhookSubscriptionResponse,
} from '../api';

interface EditWebhookModalProps {
  webhook: WebhookSubscriptionResponse | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (request: UpdateWebhookSubscriptionRequest) => void;
}

interface FormState {
  url: string;
  events: Set<WebhookEventName>;
  typeSlugs: Set<string>;
  isActive: boolean;
}

function isWebhookEventName(value: string): value is WebhookEventName {
  return (WEBHOOK_EVENTS as readonly string[]).includes(value);
}

function buildInitialState(webhook: WebhookSubscriptionResponse): FormState {
  return {
    url: webhook.url,
    events: new Set(webhook.events.filter(isWebhookEventName)),
    typeSlugs: new Set(webhook.documentTypeSlugs),
    isActive: webhook.isActive,
  };
}

/**
 * Edición in-place de un webhook existente (URL, eventos, tipos de documento, activo).
 *
 * El secret HMAC NUNCA se toca desde este modal — no hay ningún input de secret aquí.
 * Rotarlo es una acción separada y explícita (botón "Rotar secret" en la card), porque
 * invalida el secret anterior y el operador debe confirmarla conscientemente en vez de
 * que ocurra como efecto colateral de guardar otros campos.
 *
 * Igual que EditProviderModal, el body que se envía es un diff: solo se incluyen los
 * campos que cambiaron respecto al `webhook` original (ver `buildUpdateRequest`).
 */
export function EditWebhookModal({
  webhook,
  busy,
  onClose,
  onSubmit,
}: EditWebhookModalProps): JSX.Element | null {
  const [state, setState] = useState<FormState | null>(null);
  const documentTypes = useDocumentTypes();

  // Re-bootstrap el form cuando cambia el webhook que se está editando (o se cierra el modal).
  useEffect(() => {
    if (webhook === null) {
      setState(null);
      return;
    }
    setState(buildInitialState(webhook));
  }, [webhook]);

  useEffect(() => {
    if (webhook === null) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [webhook, busy, onClose]);

  if (webhook === null || state === null) {
    return null;
  }

  const valid = state.url.trim().startsWith('https://') && state.events.size > 0;

  const toggleEvent = (ev: WebhookEventName): void => {
    setState((prev) => {
      if (prev === null) return prev;
      const next = new Set(prev.events);
      if (next.has(ev)) {
        next.delete(ev);
      } else {
        next.add(ev);
      }
      return { ...prev, events: next };
    });
  };

  const toggleType = (slug: string): void => {
    setState((prev) => {
      if (prev === null) return prev;
      const next = new Set(prev.typeSlugs);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return { ...prev, typeSlugs: next };
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!valid) {
      return;
    }
    onSubmit(buildUpdateRequest(webhook, state));
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-webhook-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="edit-webhook-title" className="text-lg font-semibold text-slate-900">
              Editar webhook
            </h2>
            <p className="mt-1 truncate text-sm text-slate-500">
              <span className="font-mono">{webhook.id}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" aria-hidden strokeWidth={2} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label" htmlFor="edit-wh-url">
              URL (https obligatorio)
            </label>
            <input
              id="edit-wh-url"
              className="input"
              placeholder="https://hooks.example.com/docusense"
              value={state.url}
              onChange={(e) => setState({ ...state, url: e.target.value })}
              disabled={busy}
            />
          </div>

          <fieldset>
            <legend className="label">Eventos suscritos</legend>
            <div className="flex flex-wrap gap-3">
              {WEBHOOK_EVENTS.map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={state.events.has(ev)}
                    onChange={() => toggleEvent(ev)}
                    disabled={busy}
                  />
                  {WEBHOOK_EVENT_LABELS[ev]}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="label">Tipos de documento</legend>
            <p className="mb-2 text-xs text-slate-500">
              Sin selección = todos los tipos. Marca tipos concretos para recibir solo esos.
            </p>
            {documentTypes.isLoading ? (
              <p className="text-sm text-slate-400">Cargando tipos…</p>
            ) : (documentTypes.data ?? []).length === 0 ? (
              <p className="text-sm text-slate-400">No hay tipos de documento configurados.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {(documentTypes.data ?? []).map((dt) => (
                  <label key={dt.slug} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={state.typeSlugs.has(dt.slug)}
                      onChange={() => toggleType(dt.slug)}
                      disabled={busy}
                    />
                    {dt.name}
                    <span className="font-mono text-xs text-slate-400">{dt.slug}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200/70 bg-slate-50/60 p-3">
            <input
              type="checkbox"
              checked={state.isActive}
              onChange={(e) => setState({ ...state, isActive: e.target.checked })}
              disabled={busy}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Activo</span>
              <span className="ml-2 text-xs text-slate-500">
                Con el webhook activo, el dispatcher entrega los eventos suscritos. Si lo
                desactivas, las entregas quedan en pausa hasta que lo reactives.
              </span>
            </span>
          </label>

          <p className="rounded-lg border border-slate-200/70 bg-slate-50/60 p-3 text-xs text-slate-500">
            El secret HMAC no cambia al guardar esta edición. Para rotarlo, cierra este
            modal y usa el botón &quot;Rotar secret&quot; de la card del webhook.
          </p>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary inline-flex items-center gap-2"
            disabled={busy || !valid}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden strokeWidth={2} /> : null}
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Diff form state → request body. Solo se incluyen los campos que cambiaron respecto
 * al `webhook` original; `id` siempre se incluye porque el backend lo necesita para
 * resolver la ruta. El secret nunca forma parte de este diff — no existe campo de
 * secret en el form.
 *
 * `documentTypeSlugs` es el matiz más delicado (semántica del backend: null/omitido =
 * no tocar · [] = limpiar, vuelve a "todos los tipos" · lista = reemplazar):
 *   - Se compara el Set actual contra `webhook.documentTypeSlugs` COMO CONJUNTOS (sin
 *     importar orden). Si son iguales, se omite el campo (queda "no tocar").
 *   - Si difieren y el Set actual queda vacío, se envía `[]` (limpiar).
 *   - Si difieren y no está vacío, se envía `Array.from(typeSlugs)` (reemplaza).
 */
function buildUpdateRequest(
  webhook: WebhookSubscriptionResponse,
  state: FormState,
): UpdateWebhookSubscriptionRequest {
  const request: UpdateWebhookSubscriptionRequest = { id: webhook.id };

  const urlTrimmed = state.url.trim();
  if (urlTrimmed !== webhook.url && urlTrimmed.startsWith('https://')) {
    request.url = urlTrimmed;
  }

  if (!setsEqual(state.events, new Set(webhook.events))) {
    request.events = Array.from(state.events);
  }

  if (state.isActive !== webhook.isActive) {
    request.isActive = state.isActive;
  }

  if (!setsEqual(state.typeSlugs, new Set(webhook.documentTypeSlugs))) {
    request.documentTypeSlugs = state.typeSlugs.size > 0 ? Array.from(state.typeSlugs) : [];
  }

  return request;
}

function setsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}
